(() => {
  if (window.__pdpBundleLoaded) return;
  window.__pdpBundleLoaded = true;

  const formatMoney = (cents, format) => {
    const helper = window.Alpine && Alpine.store('xHelper');
    if (helper && helper.formatMoney) return helper.formatMoney(cents, format);
    return format.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2));
  };

  const fill = (template, values) =>
    template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  class PdpBundle {
    constructor(root) {
      this.root = root;
      this.variants = JSON.parse(root.querySelector('[data-pdp-variants]').textContent);
      this.byId = new Map(this.variants.map((v) => [v.id, v]));
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.tiers = [...root.querySelectorAll('.pdp-bundle__tier')];
      this.swatches = [...root.querySelectorAll('.pdp-bundle__swatch')];
      this.slotsEl = root.querySelector('[data-slots]');
      this.atc = root.querySelector('[data-atc]');
      this.atcText = root.querySelector('[data-atc-text]');
      this.errorEl = root.querySelector('[data-error]');
      this.activeSlot = 0;

      const urlVariant = Number(new URLSearchParams(location.search).get('variant'));
      const firstAvailable = this.variants.find((v) => v.available) || this.variants[0];
      this.defaultVariant = this.byId.get(urlVariant)?.available ? urlVariant : firstAvailable.id;

      const defaultTier =
        this.tiers.find((t) => t.dataset.tier === root.dataset.defaultTier) || this.tiers[0];
      this.slots = [this.defaultVariant];
      this.discount = 0;
      if (defaultTier) this.selectTier(defaultTier, false);

      this.tiers.forEach((t) => t.addEventListener('click', () => this.selectTier(t, true)));
      this.swatches.forEach((s) =>
        s.addEventListener('click', () => this.pickColor(Number(s.dataset.variantId)))
      );
      this.atc.addEventListener('click', () => this.addToCart());
      this.initSticky();
      this.render();
    }

    selectTier(tierEl, focusSlots) {
      const qty = Number(tierEl.dataset.qty) || 1;
      this.discount = Number(tierEl.dataset.discount) || 0;
      this.tiers.forEach((t) => t.setAttribute('aria-checked', String(t === tierEl)));
      // Keep colors already chosen; new slots repeat the last chosen color.
      const last = this.slots[this.slots.length - 1] || this.defaultVariant;
      this.slots = Array.from({ length: qty }, (_, i) => this.slots[i] || last);
      this.activeSlot = Math.min(this.activeSlot, qty - 1);
      if (focusSlots && qty > 1) this.activeSlot = Math.min(this.slots.length - 1, 1);
      this.render();
    }

    pickColor(id) {
      if (!this.byId.get(id)?.available) return;
      this.slots[this.activeSlot] = id;
      if (this.slots.length > 1 && this.activeSlot < this.slots.length - 1) this.activeSlot += 1;
      this.render();
    }

    totals() {
      const subtotal = this.slots.reduce((sum, id) => sum + this.byId.get(id).price, 0);
      const compareBase = this.slots.reduce((sum, id) => {
        const v = this.byId.get(id);
        return sum + Math.max(v.price, v.compare_at_price || 0);
      }, 0);
      const total = Math.round(subtotal * (1 - this.discount / 100));
      return { total, compare: compareBase > total ? compareBase : 0 };
    }

    render() {
      const qty = this.slots.length;
      const { total, compare } = this.totals();
      const counts = this.slots.reduce((m, id) => m.set(id, (m.get(id) || 0) + 1), new Map());

      // Tier prices
      this.tiers.forEach((t) => {
        const tq = Number(t.dataset.qty) || 1;
        const d = Number(t.dataset.discount) || 0;
        const unit = this.byId.get(this.slots[0]).price;
        const price = Math.round(unit * tq * (1 - d / 100));
        t.querySelector('[data-tier-price]').textContent = formatMoney(price, this.moneyFormat);
      });

      // Slots
      this.slotsEl.hidden = qty < 2;
      if (qty > 1) {
        const label = this.root.dataset.slotLabel || 'Item [n]';
        this.slotsEl.innerHTML = this.slots
          .map((id, i) => {
            const v = this.byId.get(id);
            return `<button type="button" class="pdp-bundle__slot" aria-pressed="${i === this.activeSlot}" data-slot="${i}">
              <img src="${v.image}" alt="" width="40" height="40">
              <span><small>${fill(label, { n: i + 1 })}</small>${v.title}</span>
            </button>`;
          })
          .join('');
        this.slotsEl.querySelectorAll('[data-slot]').forEach((b) =>
          b.addEventListener('click', () => {
            this.activeSlot = Number(b.dataset.slot);
            this.render();
          })
        );
      }

      // Swatches
      const activeId = this.slots[this.activeSlot];
      this.swatches.forEach((s) => {
        const id = Number(s.dataset.variantId);
        s.setAttribute('aria-selected', String(id === activeId));
        const badge = s.querySelector('[data-count]');
        const c = counts.get(id) || 0;
        badge.hidden = !(qty > 1 && c > 0);
        badge.textContent = `×${c}`;
      });

      const current = this.root.querySelector('[data-current-label]');
      current.textContent =
        qty > 1
          ? fill(this.root.dataset.chooseLabel || '', { n: this.activeSlot + 1 })
          : this.byId.get(activeId).title;

      // Summary
      const money = formatMoney(total, this.moneyFormat);
      this.root.querySelector('[data-total]').textContent = money;
      const compareEl = this.root.querySelector('[data-compare]');
      const saveEl = this.root.querySelector('[data-save]');
      compareEl.hidden = saveEl.hidden = !compare;
      if (compare) {
        compareEl.textContent = formatMoney(compare, this.moneyFormat);
        saveEl.textContent = `-${Math.round((1 - total / compare) * 100)}%`;
      }
      const tpl = qty > 1 ? this.root.dataset.atcLabel : this.root.dataset.atcLabelSingle;
      this.atcText.textContent = fill(tpl || '', { qty, price: money });
      const sticky = this.root.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = money;
    }

    async addToCart() {
      if (this.atc.disabled) return;
      this.atc.disabled = true;
      this.atc.classList.add('is-loading');
      this.errorEl.hidden = true;

      const grouped = this.slots.reduce((m, id) => m.set(id, (m.get(id) || 0) + 1), new Map());
      const items = [...grouped].map(([id, quantity]) => ({ id, quantity }));
      const cartHelper = window.Alpine && Alpine.store('xCartHelper');
      const sections = cartHelper ? cartHelper.getSectionsToRender().map((s) => s.id) : [];

      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message);

        if (cartHelper && data.sections) cartHelper.reRenderSections(data.sections);
        const noti = window.Alpine && Alpine.store('xCartNoti');
        if (noti && noti.enable) {
          noti.setItem(data);
        } else if (window.Alpine && Alpine.store('xMiniCart')) {
          Alpine.store('xMiniCart').openCart();
          document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
        } else {
          window.location.href = window.Shopify.routes.root + 'cart';
        }
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      } catch (err) {
        this.errorEl.textContent = err.message || this.root.dataset.errorLabel;
        this.errorEl.hidden = false;
      } finally {
        this.atc.disabled = false;
        this.atc.classList.remove('is-loading');
      }
    }

    initSticky() {
      const bar = this.root.querySelector('[data-sticky]');
      if (!bar || !('IntersectionObserver' in window)) return;
      document.body.appendChild(bar);
      const btn = bar.querySelector('[data-sticky-btn]');
      btn.addEventListener('click', () =>
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        bar.setAttribute('aria-hidden', String(!passed));
        btn.tabIndex = passed ? 0 : -1;
      }).observe(this.root);
    }
  }

  const init = (scope = document) =>
    scope.querySelectorAll('[data-pdp-bundle]:not([data-ready])').forEach((el) => {
      el.dataset.ready = 'true';
      new PdpBundle(el);
    });

  // Smooth scroll for in-page links like the viral badge.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-pdp-scroll]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => {
    document.querySelectorAll('body > .pdp-bundle__sticky').forEach((el) => el.remove());
    init(e.target);
  });
})();
